import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import { AuthContext } from '../../contexts/auth-context';
import { DocumentAgentData, DocumentCarrierData } from '../../types';
import { captureError } from '../../utils/capture-error';
import { ContractDocuSign } from '../contract/contract-docusign';
import { ContractQuestions } from '../contract/contract-questions';
import { ContractStartedQuestions } from '../contract/contract-started-questions';
import { RouteError } from '../route-container/route-error';
import { TaxDocPDF } from './tax-doc-pdf';

export function TaxDoc(props: {
  open: boolean;
  doc: DocumentCarrierData;
  onClose: () => void;
  onSuccess: () => void;
}) {
  // Context
  const {
    state: { user, impersonatedAgent, session },
  } = React.useContext(AuthContext);
  // State
  const [fetching, setFetching] = React.useState({
    Closing: false,
  });
  const [AgtDocID, setAgtDocID] = React.useState<number | null>(null);
  const [createdUrl, setCreatedUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (props.open && props.doc) {
      setAgtDocID(props.doc.AgtDocID);
      setCreatedUrl(null);
      setFetching({ Closing: false });
    }
  }, [props.open, props.doc]);

  const queryClient = useQueryClient();

  const AgtNo =
    impersonatedAgent?.AgtNo ||
    session?.getAccessToken().decodePayload()['myStaffForAgt'] ||
    user?.getUsername();

  // Query - Create Agent Tax Document
  const pathCreate = '/documents/create';
  const DocID = props.doc?.DocID;
  const queryCreate = useQuery({
    enabled: Boolean(
      props.open &&
        props.doc &&
        props.doc.Signed === null &&
        AgtDocID !== undefined &&
        AgtDocID === null &&
        !props.doc.HasFormQuestions,
    ),
    queryKey: [pathCreate, { DocID }],
    queryFn: async () => {
      const response: {
        data: DocumentAgentData | null;
      } = await API.post('ContractingAPI', pathCreate, {
        body: { DocID, HostUrl: window.location.origin },
      });

      return response.data || null;
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Query - Get Agent Tax Document
  const path = '/documents/get';
  const query = useQuery({
    enabled: Boolean(
      props.open &&
        props.doc &&
        AgtDocID !== undefined &&
        AgtDocID !== null &&
        !createdUrl,
    ),
    queryKey: [path, { AgtDocID }],
    queryFn: async () => {
      const response: {
        data: DocumentAgentData | null;
      } = await API.post('ContractingAPI', path, {
        body: { AgtDocID, HostUrl: window.location.origin },
      });

      return response.data || null;
    },
    onError: (error) => captureError({ data: { error } }),
  });

  const handleClose = async () => {
    try {
      setFetching((currentState) => ({ ...currentState, Closing: true }));

      if (!props.doc.Signed) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }

      // Refetch Agreement status
      await queryClient.invalidateQueries({
        queryKey: ['/documents/list', { DocTitle: 'Agent W9' }],
      });

      // Refetch Getting Started
      await queryClient.invalidateQueries({
        queryKey: ['/agent/getting-started', { AgtNo }],
      });

      props.onClose();
      setAgtDocID(null);
    } catch (error) {
      captureError({ data: { error } });
    } finally {
      setFetching((currentState) => ({ ...currentState, Closing: false }));
    }
  };

  // Set AgtDocID and SignerURL after document is created
  const handleCreate = (id: number, url: string | null) => {
    setCreatedUrl(url);
    setAgtDocID(id);
  };

  let content = <Box />;

  const signerUrl = createdUrl || query.data?.SignerUrl || null;

  if (fetching.Closing || query.isFetching || queryCreate.isFetching) {
    content = <LinearProgress />;
  } else if (query.isError || queryCreate.isError) {
    content = <RouteError />;
  } else if (signerUrl) {
    content = <ContractDocuSign url={signerUrl} />;
  } else if (!AgtDocID && props.doc && props.doc.HasFormQuestions) {
    content = <ContractQuestions doc={props.doc} onCreate={handleCreate} />;
  } else if (query.data && query.data.Filename && AgtDocID && !signerUrl) {
    content = <TaxDocPDF AgtDocID={AgtDocID} />;
  }

  // Display Form Questions for contract
  let formQuestions: JSX.Element | null = null;
  if (query.data && query.data.HasFormQuestions) {
    formQuestions = <ContractStartedQuestions data={query.data} Title="Form" />;
  }

  const isWorking = fetching.Closing || Boolean(AgtDocID && query.isFetching);

  return (
    <Drawer
      container={window.document.body}
      variant="temporary"
      anchor="right"
      open={props.open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: { xs: '100vw', md: '85vw', lg: '75vw', xl: '65vw' },
          height: '100dvh',
        },
      }}
    >
      <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
        <Toolbar>
          <IconButton disabled={isWorking} sx={{ mr: 2 }} onClick={handleClose}>
            {isWorking ? <CircularProgress size={24} /> : <CloseIcon />}
          </IconButton>

          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ px: 1, flex: 1 }}>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  fontSize: { xs: 14, sm: 18, md: 22 },
                  fontWeight: 'normal',
                }}
              >
                Tax Document (W-9)
              </Typography>
            </Box>
          </Box>
        </Toolbar>

        <Divider />
        {formQuestions}
        {content}
      </Box>
    </Drawer>
  );
}
